/* Product card quick add (hover, desktop)
   Option selection, validation and add to cart for the panel rendered by
   snippets/product-card-quick-add.liquid inside each product card.
   - A value is disabled when no available variant has it. A value that is not
     available with the other options already chosen stays clickable but marked
     (.is-unavailable); choosing it clears the conflicting choices.
   - Options with a single available value (e.g. one colour) are pre-selected.
   - "Agregar al carrito" without every option -> "Selecciona: Talla" plus a
     shake. With a full selection the form is submitted to <product-form>
     (assets/product-form.js), which adds through the cart store and opens the
     cart drawer.
   - GA4: pushes add_to_cart once the cart store confirms the add
     (on:cart:loaded, type ADD, same variant). */
class ProductCardQuickAdd extends HTMLElement {
    #initialized = false;
    #errorTimer = 0;
    connectedCallback() {
        if (this.#initialized) return;
        this.#initialized = true;
        try {
            this.variants = JSON.parse(this.querySelector("[data-pqa-variants]").textContent);
        } catch (error) {
            this.variants = [];
        }
        this.groups = Array.from(this.querySelectorAll("[data-pqa-option]"));
        this.selected = this.groups.map((() => null));
        this.form = this.querySelector("form");
        this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
        this.error = this.querySelector("[data-pqa-error]");
        this.addEventListener("click", this.#onClick.bind(this));
        this.groups.forEach(((group, index) => {
            const available = this.#buttons(index).filter((button => !button.disabled));
            if (available.length === 1) this.selected[index] = available[0].dataset.pqaValue;
        }));
        this.#refresh();
    }
    #buttons(index) {
        return Array.from(this.groups[index].querySelectorAll("[data-pqa-value]"));
    }
    #matches(variant, index, value) {
        return variant.options[index] === value && this.selected.every(((selected, j) => j === index || selected === null || variant.options[j] === selected));
    }
    #onClick(event) {
        const valueButton = event.target.closest("[data-pqa-value]");
        if (valueButton) {
            event.preventDefault();
            if (valueButton.disabled) return;
            const index = this.groups.indexOf(valueButton.closest("[data-pqa-option]"));
            const value = valueButton.dataset.pqaValue;
            this.selected[index] = value;
            // Drop choices of other options that have no available variant with this value.
            this.selected = this.selected.map(((selected, j) => {
                if (j === index || selected === null) return selected;
                const stillPossible = this.variants.some((variant => variant.available && variant.options[index] === value && variant.options[j] === selected));
                return stillPossible ? selected : null;
            }));
            this.#hideError();
            this.#refresh();
            return;
        }
        const submit = event.target.closest("[data-pqa-submit]");
        if (submit) {
            event.preventDefault();
            this.#add(submit);
        }
    }
    #refresh() {
        this.groups.forEach(((group, index) => {
            this.#buttons(index).forEach((button => {
                const value = button.dataset.pqaValue;
                const pressed = this.selected[index] === value;
                const possible = this.variants.some((variant => variant.available && this.#matches(variant, index, value)));
                button.setAttribute("aria-pressed", String(pressed));
                button.classList.toggle("is-selected", pressed);
                button.classList.toggle("is-unavailable", !button.disabled && !possible);
            }));
        }));
        const variant = this.#currentVariant();
        if (this.idInput && this.groups.length > 0) this.idInput.value = variant ? variant.id : "";
    }
    #currentVariant() {
        if (this.groups.length === 0) return this.variants[0] || null;
        if (this.selected.some((selected => selected === null))) return null;
        return this.variants.find((variant => this.selected.every(((selected, j) => variant.options[j] === selected)))) || null;
    }
    #add(button) {
        const missing = this.groups.filter(((group, index) => this.selected[index] === null)).map((group => group.dataset.optionName));
        if (missing.length > 0) {
            this.#showError(`${this.dataset.errorPrefix || ""} ${missing.join(", ")}`.trim(), button);
            return;
        }
        const variant = this.#currentVariant();
        if (!variant || !variant.available) {
            this.#showError(this.dataset.errorUnavailable || "", button);
            return;
        }
        if (!this.form || this.classList.contains("is-busy")) return;
        this.idInput.value = variant.id;
        this.#track(variant);
        if (typeof this.form.requestSubmit === "function") {
            this.form.requestSubmit();
        } else {
            this.form.dispatchEvent(new Event("submit", {
                bubbles: true,
                cancelable: true
            }));
        }
    }
    #track(variant) {
        this.classList.add("is-busy");
        let timer = 0;
        const done = () => {
            document.removeEventListener("on:cart:loaded", onLoaded);
            document.removeEventListener("on:cart:failed", onFailed);
            clearTimeout(timer);
            setTimeout((() => this.classList.remove("is-busy")), 600);
        };
        const onLoaded = event => {
            const detail = event.detail || {};
            if (detail.type !== "ADD" || !(detail.items || []).some((item => String(item.id) === String(variant.id)))) return;
            done();
            this.#pushAddToCart(variant);
        };
        const onFailed = event => {
            if ((event.detail || {}).type === "ADD") done();
        };
        document.addEventListener("on:cart:loaded", onLoaded);
        document.addEventListener("on:cart:failed", onFailed);
        timer = setTimeout(done, 15e3);
    }
    #pushAddToCart(variant) {
        const price = Math.round(variant.price) / 100;
        const item = {
            item_id: Number(this.dataset.productId) || this.dataset.productId,
            item_name: this.dataset.productTitle || "",
            item_variant: variant.title,
            item_brand: this.dataset.productVendor || "",
            item_category: this.dataset.productType || "",
            price: price,
            quantity: 1
        };
        if (this.dataset.listName) item.item_list_name = this.dataset.listName;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            ecommerce: null
        });
        window.dataLayer.push({
            event: "add_to_cart",
            addToCartSource: "product_card_quick_add",
            ecommerce: {
                currency: window.Shopify && window.Shopify.currency && window.Shopify.currency.active || this.dataset.currency,
                value: price,
                items: [ item ]
            }
        });
    }
    #showError(message, button) {
        if (this.error) {
            this.error.textContent = message;
            this.error.removeAttribute("hidden");
            clearTimeout(this.#errorTimer);
            this.#errorTimer = setTimeout((() => this.#hideError()), 3500);
        }
        if (button) {
            button.classList.remove("is-shaking");
            void button.offsetWidth;
            button.classList.add("is-shaking");
            button.addEventListener("animationend", (() => button.classList.remove("is-shaking")), {
                once: true
            });
        }
    }
    #hideError() {
        if (this.error) this.error.setAttribute("hidden", "");
    }
}

if (!customElements.get("product-card-quick-add")) {
    customElements.define("product-card-quick-add", ProductCardQuickAdd);
}
